import React, { useState, useEffect } from 'react'
import Alert from './shared/Alert'
import CurrencyInput from './shared/CurrencyInput'
import SSNInput from './shared/SSNInput'
import PhoneNumberInput from './shared/PhoneNumberInput'
import LawFirmSearch from './shared/LawFirmSearch'
import Tooltip from './shared/Tooltip'
import GooglePlacesAutocomplete from './shared/GooglePlacesAutocomplete'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'

// Helper function for email validation
const isValidEmail = email => {
  // JavaScript equivalent of Ruby's URI::MailTo::EMAIL_REGEXP
  // This regex is consistent with the Rails backend validation
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

  return emailRegex.test(email.trim())
}

const DocumentSummaryRoot = () => {
  const [responses, setResponses] = useState(null)
  const [supportedJurisdictions, setSupportedJurisdictions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [additionalLawyerEmails, setAdditionalLawyerEmails] = useState([])
  const [documentId, setDocumentId] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [showError, setShowError] = useState(false)
  const [lawFirmSearchResults, setLawFirmSearchResults] = useState([])
  const [lawFirmSearchLoading, setLawFirmSearchLoading] = useState(false)
  const [selectedLawFirm, setSelectedLawFirm] = useState(null)
  const [plaintiffSearchResults, setPlaintiffSearchResults] = useState([])
  const [plaintiffSearchLoading, setPlaintiffSearchLoading] = useState(false)
  const [selectedPlaintiff, setSelectedPlaintiff] = useState(null)
  const [isPrelit, setIsPrelit] = useState(false)

  // convenient map between the form field names and the step names
  const fieldMapping = {
    customer_name: 'customerName',
    lawsuit_state: 'lawsuitState',
    lawsuit_amount: 'lawsuitAmount',
    civil_number: 'civilNumber',
    lawyer_email: 'lawyerEmail',
    law_firm_name: 'lawFirmName',
    plaintiff_name: 'plaintiffName',
    customer_mailing_address: 'address',
    customer_phone_number: 'phoneNumber',
    social_security_number: 'ssn',
    birthdate: 'birthdate',
  }

  // Add validation state to track which fields have been successfully saved
  const [validatedFields, setValidatedFields] = useState({
    customerName: false,
    lawsuitState: false,
    lawsuitAmount: false,
    civilNumber: false,
    lawyerEmail: false,
    lawFirmName: false,
    plaintiffName: false,
    address: false,
    phoneNumber: false,
    ssn: false,
    birthdate: false,
  })

  // Define required fields
  const requiredFields = [
    'customerName',
    'lawsuitState',
    'lawsuitAmount',
    'civilNumber',
    'plaintiffName',
    'ssn',
    'birthdate',
    'address',
    'phoneNumber',
    'lawFirmName',
    'lawyerEmail',
  ]

  // Add form state variables
  const [formData, setFormData] = useState({
    customerName: '',
    lawsuitState: '',
    lawsuitAmount: '',
    civilNumber: '',
    lawyerEmail: '',
    lawFirmName: '',
    plaintiffName: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
    },
    phoneNumber: '',
    ssn: {
      fullSSN: '',
      lastFour: '',
      showFullSSN: true,
    },
    birthdate: '',
  })

  // Add state for checkbox
  const [confirmInfoAccuracy, setConfirmInfoAccuracy] = useState(false)

  // Add state to track original values for comparison
  const [originalFormData, setOriginalFormData] = useState({
    customerName: '',
    lawsuitState: '',
    lawsuitAmount: '',
    civilNumber: '',
    lawyerEmail: '',
    lawFirmName: '',
    plaintiffName: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
    },
    phoneNumber: '',
    ssn: {
      fullSSN: '',
      lastFour: '',
      showFullSSN: true,
    },
    birthdate: '',
  })

  // Function to show success message
  const showSuccessMessage = field => {
    setSaveMessage(`${field} saved successfully`)
    setSaveSuccess(true)

    // Hide the message after 3 seconds
    setTimeout(() => {
      setSaveSuccess(false)
    }, 3000)
  }

  // Helper function to deep compare values
  const valuesAreEqual = (value1, value2) => {
    if (
      typeof value1 === 'object' &&
      typeof value2 === 'object' &&
      value1 !== null &&
      value2 !== null
    ) {
      return JSON.stringify(value1) === JSON.stringify(value2)
    }
    return value1 === value2
  }

  // Helper function to get original value for a field
  const getOriginalValue = fieldName => {
    if (fieldName === 'address') {
      return originalFormData.address
    } else if (fieldName === 'ssn') {
      return originalFormData.ssn
    } else {
      return originalFormData[fieldName]
    }
  }

  // Helper function to update original values after successful save
  const updateOriginalValue = (fieldName, newValue) => {
    setOriginalFormData(prev => {
      if (fieldName === 'address') {
        return {
          ...prev,
          address: { ...newValue },
        }
      } else if (fieldName === 'ssn') {
        return {
          ...prev,
          ssn: { ...newValue },
        }
      } else {
        return {
          ...prev,
          [fieldName]: newValue,
        }
      }
    })
  }

  // Add function to show error message in the same style
  const showErrorMessage = message => {
    setErrorMessage(message)
    setShowError(true)

    // Hide the message after 5 seconds
    setTimeout(() => {
      setShowError(false)
    }, 5000)
  }

  // Function to save data immediately without debouncing
  const saveData = (stepName, inputData, encrypted = false, fieldName) => {
    if (!documentId) return

    // Get the actual input key from the inputData object
    const inputKey = Object.keys(inputData)[0]
    const inputValue = inputData[inputKey]

    // Get the field name for comparison
    const formFieldName = fieldMapping[stepName]

    // For special cases, extract the relevant value for comparison
    let currentValue, originalValue

    if (stepName === 'customer_mailing_address') {
      currentValue = {
        street: inputData.customer_street || '',
        city: inputData.customer_city || '',
        state: inputData.customer_state || '',
        zipCode: inputData.customer_zip_code || '',
      }
      originalValue = originalFormData.address
    } else if (stepName === 'social_security_number') {
      currentValue = {
        fullSSN: inputData.social_security_number || '',
        lastFour: inputData.social_security_number_last_four || '',
      }
      originalValue = {
        fullSSN: originalFormData.ssn.fullSSN,
        lastFour: originalFormData.ssn.lastFour,
      }
    } else if (stepName === 'lawsuit_amount') {
      // For lawsuit amount, compare the formatted currency value
      const currentAmount = parseFloat(inputData.lawsuit_amount.replace(/[$,]/g, '')) || 0
      const originalAmount = parseFloat(originalFormData.lawsuitAmount) || 0
      currentValue = currentAmount
      originalValue = originalAmount
    } else {
      // For simple fields, get the value directly
      currentValue = inputValue
      originalValue = originalFormData[formFieldName]
    }

    // Check if value has actually changed BEFORE checking if it's blank
    // This prevents unnecessary saves and error messages when focusing on blank fields
    if (valuesAreEqual(currentValue, originalValue)) {
      // Only mark as valid if the field actually has a value AND is a required field
      // or if it's not a required field (optional fields can be blank and valid)
      if (fieldMapping[stepName]) {
        const fieldName = fieldMapping[stepName]
        const isRequired = requiredFields.includes(fieldName)
        const hasValue = inputValue && inputValue.trim() !== ''

        // For required fields: only mark valid if has value
        // For optional fields: mark valid regardless
        if (!isRequired || hasValue) {
          setValidatedFields(prev => ({
            ...prev,
            [fieldName]: true,
          }))
        }
        // If required field is blank, don't change validation state
      }
      return
    }

    // Only check for blank values if the value has actually changed
    // Special handling for SSN since it has two fields
    if (stepName === 'social_security_number') {
      const hasFullSSN =
        inputData.social_security_number && inputData.social_security_number.trim() !== ''
      const hasLastFour =
        inputData.social_security_number_last_four &&
        inputData.social_security_number_last_four.trim() !== ''

      if (!hasFullSSN && !hasLastFour) {
        showErrorMessage('Please provide either your full SSN or the last 4 digits')
        setValidatedFields(prev => ({
          ...prev,
          ssn: 'invalid',
        }))
        return
      }
    } else if (inputValue === '') {
      showErrorMessage('Input cannot be blank.')
      setValidatedFields(prev => ({
        ...prev,
        [fieldMapping[stepName]]: 'invalid',
      }))
      return
    }

    updateWalkThroughStepResponse(stepName, inputData, encrypted)
      .then(data => {
        // Mark the field as validated
        if (fieldMapping[stepName] && !data.errors) {
          setValidatedFields(prev => ({
            ...prev,
            [fieldMapping[stepName]]: true,
          }))

          // Update the original value to the new saved value
          if (stepName === 'customer_mailing_address') {
            updateOriginalValue('address', currentValue)
          } else if (stepName === 'social_security_number') {
            updateOriginalValue('ssn', {
              fullSSN: inputData.social_security_number || originalFormData.ssn.fullSSN,
              lastFour: inputData.social_security_number_last_four || originalFormData.ssn.lastFour,
              showFullSSN: originalFormData.ssn.showFullSSN,
            })
          } else {
            updateOriginalValue(formFieldName, currentValue)
          }

          showSuccessMessage(fieldName || stepName)
        } else {
          setValidatedFields(prev => ({
            ...prev,
            [fieldMapping[stepName]]: 'invalid',
          }))
        }
      })
      .catch(err => {
        console.error(`Error saving ${stepName}:`, err)
      })
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Extract document ID from URL
        const urlPath = window.location.pathname
        const match = urlPath.match(/\/solosettles\/(\d+)/)

        if (!match || !match[1]) {
          throw new Error('Could not extract document ID from URL')
        }

        const docId = match[1]
        setDocumentId(docId)

        // Make API request using fetch instead of axios
        const response = await fetch(
          `/api_controller/get_extracted_walk_through_step_responses/${docId}`
        )

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }

        const data = await response.json()
        setResponses(data)

        if (
          data &&
          data.extracted_step_values &&
          data.extracted_step_values.determine_if_lawsuit_exists &&
          data.extracted_step_values.determine_if_lawsuit_exists.inputs &&
          data.extracted_step_values.determine_if_lawsuit_exists.inputs
            .determine_if_lawsuit_exists === 'no'
        ) {
          setIsPrelit(true)
        }

        // Initialize form data from responses
        if (data && data.extracted_step_values) {
          const values = data.extracted_step_values

          // Initialize selected law firm if there is one
          if (
            values.confirm_law_firm &&
            values.confirm_law_firm.formatted_value &&
            values.confirm_law_firm.formatted_value.id
          ) {
            setSelectedLawFirm({
              id: values.confirm_law_firm.formatted_value.id,
              name: values.confirm_law_firm.formatted_value.name,
              to_addresses: values.confirm_law_firm.formatted_value.to_addresses,
            })
          }

          // Initialize selected plaintiff if there is one
          if (
            values.confirm_plaintiff &&
            values.confirm_plaintiff.formatted_value &&
            values.confirm_plaintiff.formatted_value.id
          ) {
            setSelectedPlaintiff({
              id: values.confirm_plaintiff.formatted_value.id,
              name: values.confirm_plaintiff.formatted_value.name,
            })
          }

          // Parse lawyer emails from semicolon-delimited string
          let primaryEmail = ''
          let additionalEmails = []

          if (
            values.lawyer_email.formatted_value &&
            Array.isArray(values.lawyer_email.formatted_value)
          ) {
            // Handle case where formatted_value is already an array
            if (values.lawyer_email.formatted_value.length > 0) {
              // Don't actually set the emails in the form if we have them stored
              // This prevents us from sending the full email list to the client
              // We'll just flag that we have some emails
              if (values.confirm_law_firm?.formatted_value?.to_addresses) {
                primaryEmail = ''
              } else {
                primaryEmail = values.lawyer_email.formatted_value[0] || ''
                additionalEmails = values.lawyer_email.formatted_value.slice(1) || []
              }
            }
          } else if (values.lawyer_email.inputs && values.lawyer_email.inputs.lawyer_emails) {
            // Handle case where it's a semicolon-delimited string
            // Don't actually set the emails in the form if we have them stored
            // This prevents us from sending the full email list to the client
            if (values.confirm_law_firm?.formatted_value?.to_addresses) {
              primaryEmail = ''
            } else {
              const emailsArray = values.lawyer_email.inputs.lawyer_emails
                .split(';')
                .filter(email => email.trim() !== '')
              if (emailsArray.length > 0) {
                primaryEmail = emailsArray[0]
                additionalEmails = emailsArray.slice(1)
              }
            }
          }

          setAdditionalLawyerEmails(additionalEmails)

          // Determine if we have a full SSN or just last 4 digits
          let fullSSN = ''
          let lastFour = ''
          let showFullSSN = true

          if (values.social_security_number && values.social_security_number.inputs) {
            // Get both values from inputs (encrypted_inputs in the backend)
            const inputs = values.social_security_number.inputs

            if (inputs.social_security_number) {
              fullSSN = inputs.social_security_number
              showFullSSN = true
            }

            if (inputs.social_security_number_last_four) {
              lastFour = inputs.social_security_number_last_four
              // Only show last four input if we don't have a full SSN
              if (!fullSSN) {
                showFullSSN = false
              }
            }
          } else if (
            values.social_security_number &&
            values.social_security_number.formatted_value
          ) {
            const ssnValue = values.social_security_number.formatted_value

            // Check if it's likely a last 4 format (4 digits or less)
            if (ssnValue.replace(/\D/g, '').length <= 4) {
              lastFour = ssnValue.replace(/\D/g, '')
              showFullSSN = false
            } else {
              fullSSN = ssnValue
            }
          }

          // Check if phone number exists
          let phoneNumber = ''

          if (values.customer_phone_number) {
            if (values.customer_phone_number.formatted_value) {
              phoneNumber = values.customer_phone_number.formatted_value
            } else if (
              values.customer_phone_number.inputs &&
              values.customer_phone_number.inputs.customer_phone_number
            ) {
              phoneNumber = values.customer_phone_number.inputs.customer_phone_number
            }
          }

          // Prepare the form data
          const newFormData = {
            customerName: values.customer_name.formatted_value || '',
            lawsuitState: values.lawsuit_state.formatted_value || '',
            lawsuitAmount:
              values.lawsuit_amount.formatted_value &&
              values.lawsuit_amount.formatted_value.cents > 0
                ? values.lawsuit_amount.formatted_value.cents / 100
                : '',
            civilNumber: values.civil_number.formatted_value || '',
            lawyerEmail: primaryEmail,
            lawFirmName: values.law_firm_name.formatted_value || '',
            plaintiffName: values.plaintiff_name.formatted_value || '',
            address: {
              street: values.customer_mailing_address.inputs?.customer_street || '',
              city: values.customer_mailing_address.inputs?.customer_city || '',
              state: values.customer_mailing_address.inputs?.customer_state || '',
              zipCode: values.customer_mailing_address.inputs?.customer_zip_code || '',
            },
            phoneNumber: phoneNumber,
            ssn: {
              fullSSN: fullSSN,
              lastFour: lastFour,
              showFullSSN: showFullSSN,
            },
            birthdate: values.birthdate.formatted_value || values.birthdate.inputs?.birthdate || '',
          }

          setFormData(newFormData)

          // Initialize validation states for pre-filled fields
          const initialValidationState = {}

          // For each field, determine if it should be valid, invalid, or neutral
          Object.keys(validatedFields).forEach(field => {
            let hasValue = false

            // Check if field has a value
            if (field === 'address') {
              hasValue = !!(
                newFormData.address.street ||
                newFormData.address.city ||
                newFormData.address.state ||
                newFormData.address.zipCode
              )
            } else if (field === 'ssn') {
              // For SSN, use the isSSNValid logic directly
              const fullSSNDigits = newFormData.ssn.fullSSN.replace(/\D/g, '')
              const lastFourDigits = newFormData.ssn.lastFour.replace(/\D/g, '')
              hasValue = fullSSNDigits.length === 9 || lastFourDigits.length === 4
            } else if (field === 'lawsuitAmount') {
              const amount = parseFloat(newFormData.lawsuitAmount) || 0
              hasValue = amount >= 100
            } else {
              hasValue = !!newFormData[field]
            }

            // On initial load, only mark fields as valid if they have values
            // Never mark as invalid on initial load - let user interaction determine that
            initialValidationState[field] = hasValue ? true : false
          })

          setValidatedFields(initialValidationState)

          // Set original form data to track changes
          setOriginalFormData(newFormData)
        }

        setLoading(false)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError(err.message || 'An error occurred while fetching data')
        setLoading(false)
      }
    }

    const fetchSupportedJurisdictions = async () => {
      const response = await fetch('/api_controller/get_supported_jurisdictions')
      const data = await response.json()
      setSupportedJurisdictions(data.supported_jurisdictions)
    }

    fetchData()
    fetchSupportedJurisdictions()
  }, [])

  // Add helper function to get headers
  const getHeaders = (contentType = 'application/json') => {
    const headers = {}
    if (contentType) {
      headers['Content-Type'] = contentType
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
    }

    return headers
  }

  // Update updateWalkThroughStepResponse to use getHeaders
  const updateWalkThroughStepResponse = async (stepName, inputData, encrypted = false) => {
    if (!documentId) return Promise.reject('No document ID')

    try {
      const response = await fetch(
        `/api_controller/update_walk_through_step_response/${documentId}`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            step_name: stepName,
            inputs: inputData,
            encrypted: encrypted,
          }),
        }
      )

      const data = await response.json()

      if (data.errors) {
        showErrorMessage(data.errors[0] || 'The information entered is invalid.')
      }

      return data
    } catch (err) {
      console.error(`Error updating ${stepName}:`, err)
      throw err
    }
  }

  // Update handlers to use immediate save on blur
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    })
  }

  const handleInputBlur = (field, value) => {
    // Get original value for comparison
    const originalValue = getOriginalValue(field)

    // Only validate if the value has actually changed
    const hasChanged = !valuesAreEqual(value, originalValue)

    if (hasChanged) {
      // Check if value is empty for required field
      const isEmpty = !value || value.trim() === ''
      if (isEmpty && requiredFields.includes(field)) {
        setValidatedFields(prev => ({
          ...prev,
          [field]: 'invalid',
        }))
      }
    }

    // Map field names to step names and input data
    const fieldToStepMap = {
      customerName: { step: 'customer_name', data: { customer_name: value }, label: 'Your Name' },
      lawsuitState: {
        step: 'lawsuit_state',
        data: { lawsuit_state: value },
        label: 'Lawsuit State',
      },
      lawsuitAmount: {
        step: 'lawsuit_amount',
        data: { lawsuit_amount: value },
        label: 'Lawsuit Amount',
      },
      civilNumber: { step: 'civil_number', data: { civil_number: value }, label: 'Civil Number' },
      lawFirmName: {
        step: 'law_firm_name',
        data: { law_firm_name: value },
        label: 'Law Firm Name',
      },
      plaintiffName: {
        step: 'plaintiff_name',
        data: { plaintiff_name: value },
        label: 'Plaintiff Name',
      },
      phoneNumber: {
        step: 'customer_phone_number',
        data: { customer_phone_number: value },
        label: 'Phone Number',
      },
      birthdate: { step: 'birthdate', data: { birthdate: value }, label: 'Birthdate' },
    }

    if (fieldToStepMap[field]) {
      saveData(
        fieldToStepMap[field].step,
        fieldToStepMap[field].data,
        false,
        fieldToStepMap[field].label
      )
    }
  }

  // Add a new function to check if SSN is valid
  const isSSNValid = () => {
    // Check if we have a valid full SSN (9 digits with or without dashes)
    const fullSSNDigits = formData.ssn.fullSSN.replace(/\D/g, '')
    if (fullSSNDigits.length === 9) {
      return true
    }

    // Check if we have valid last 4 digits (exactly 4 digits)
    const lastFourDigits = formData.ssn.lastFour.replace(/\D/g, '')
    if (lastFourDigits.length === 4) {
      return true
    }

    // Neither is valid
    return false
  }

  const handleSSNChange = e => {
    const { name, value } = e.target

    // Update local state
    if (name === 'metadata.ssn') {
      // For full SSN
      setFormData(prevData => ({
        ...prevData,
        ssn: {
          ...prevData.ssn,
          fullSSN: value,
        },
      }))
    } else if (name === 'metadata.ssn_last_four') {
      // For last four digits
      setFormData(prevData => ({
        ...prevData,
        ssn: {
          ...prevData.ssn,
          lastFour: value,
        },
      }))
    }
  }

  const handleSSNBlur = () => {
    const fullSSN = formData.ssn.fullSSN
    const lastFour = formData.ssn.lastFour

    // Strip non-digits for validation
    const fullSSNDigits = fullSSN.replace(/\D/g, '')
    const lastFourDigits = lastFour.replace(/\D/g, '')

    // Check if values have changed from original
    const originalFullSSNDigits = originalFormData.ssn.fullSSN.replace(/\D/g, '')
    const originalLastFourDigits = originalFormData.ssn.lastFour.replace(/\D/g, '')

    const hasChanged =
      fullSSNDigits !== originalFullSSNDigits || lastFourDigits !== originalLastFourDigits

    if (!hasChanged) {
      // Still validate if we have valid data
      if (fullSSNDigits.length === 9 || lastFourDigits.length === 4) {
        setValidatedFields(prev => ({
          ...prev,
          ssn: true,
        }))
      }
      return
    }

    // Validate based on what's provided
    if (fullSSNDigits.length > 0 && fullSSNDigits.length !== 9) {
      // Invalid full SSN
      setValidatedFields(prev => ({
        ...prev,
        ssn: 'invalid',
      }))
      showErrorMessage('The social security number must be 9 digits')
      return
    }

    if (lastFourDigits.length > 0 && lastFourDigits.length !== 4) {
      // Invalid last 4
      setValidatedFields(prev => ({
        ...prev,
        ssn: 'invalid',
      }))
      showErrorMessage('Please enter the last 4 digits of your social security number')
      return
    }

    if (fullSSNDigits.length === 0 && lastFourDigits.length === 0) {
      // Both empty
      setValidatedFields(prev => ({
        ...prev,
        ssn: 'invalid',
      }))
      showErrorMessage('Please provide either your full SSN or the last 4 digits')
      return
    }

    // Valid input - prepare data for saving
    let dataToSave = {}

    if (fullSSNDigits.length === 9) {
      // Extract last 4 from full SSN
      const extractedLastFour = fullSSNDigits.substring(5, 9)
      dataToSave = {
        social_security_number: fullSSN,
        social_security_number_last_four: extractedLastFour,
      }

      // Update local state with extracted last 4
      setFormData(prevData => ({
        ...prevData,
        ssn: {
          ...prevData.ssn,
          lastFour: extractedLastFour,
        },
      }))
    } else if (lastFourDigits.length === 4) {
      // Only last 4 provided
      dataToSave = {
        social_security_number: '',
        social_security_number_last_four: lastFour,
      }
    }

    // Save the data
    saveData('social_security_number', dataToSave, true, 'Social Security Number')
  }

  const addLawyerEmail = () => {
    setAdditionalLawyerEmails([...additionalLawyerEmails, ''])
  }

  const updateLawyerEmail = (index, value) => {
    const updatedEmails = [...additionalLawyerEmails]
    updatedEmails[index] = value
    setAdditionalLawyerEmails(updatedEmails)
  }

  const handleLawyerEmailBlur = () => {
    const allEmails = [formData.lawyerEmail, ...additionalLawyerEmails].filter(
      email => email.trim() !== ''
    )
    const emailString = allEmails.join(';')

    // Get original lawyer emails for comparison
    const originalAllEmails = [originalFormData.lawyerEmail, ...additionalLawyerEmails].filter(
      email => email.trim() !== ''
    )
    const originalEmailString = originalAllEmails.join(';')

    const isInvalid = allEmails.some(email => !isValidEmail(email))
    if (isInvalid) {
      setValidatedFields(prev => ({
        ...prev,
        lawyerEmail: 'invalid',
      }))
      showErrorMessage('Please enter valid email addresses')
      return
    }

    // Clear invalid state if all emails are now valid
    if (validatedFields.lawyerEmail === 'invalid' && !isInvalid && allEmails.length > 0) {
      setValidatedFields(prev => ({
        ...prev,
        lawyerEmail: true,
      }))
    }

    // Only save if emails have changed
    if (emailString !== originalEmailString) {
      saveData('lawyer_email', { lawyer_emails: emailString }, false, 'Lawyer Email')

      // Update original value after save attempt
      updateOriginalValue('lawyerEmail', formData.lawyerEmail)
    } else {
      // Only mark as valid if emails are present AND all are valid
      if (emailString.trim() !== '' && !isInvalid) {
        setValidatedFields(prev => ({
          ...prev,
          lawyerEmail: true,
        }))
      }
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    // Prevent submission if checkbox isn't checked
    if (!confirmInfoAccuracy) {
      // Use the new error message display
      showErrorMessage('Please confirm that the information is correct and accurate')
      return
    }

    // Check if all required fields are filled and valid
    const missingFields = []
    const invalidFields = []

    // Filter required fields based on current state
    const currentlyRequiredFields = requiredFields.filter(field => {
      if (field === 'lawyerEmail') {
        return shouldShowLawyerEmail()
      }
      return true
    })

    currentlyRequiredFields.forEach(field => {
      let isEmpty = false
      let isInvalid = false

      if (field === 'address') {
        isEmpty =
          (!formData.address.street || formData.address.street.trim() === '') &&
          (!formData.address.city || formData.address.city.trim() === '') &&
          (!formData.address.state || formData.address.state.trim() === '') &&
          (!formData.address.zipCode || formData.address.zipCode.trim() === '')
      } else if (field === 'ssn') {
        isEmpty = !isSSNValid()
      } else if (field === 'lawsuitAmount') {
        const amount = parseFloat(formData.lawsuitAmount) || 0
        isEmpty = amount < 100
      } else if (field === 'lawyerEmail') {
        // Only validate lawyer email if the section should be shown
        if (shouldShowLawyerEmail()) {
          const allEmails = [formData.lawyerEmail, ...additionalLawyerEmails].filter(
            email => email.trim() !== ''
          )
          isEmpty = allEmails.length === 0
          if (!isEmpty) {
            const hasInvalidEmails = allEmails.some(email => !isValidEmail(email))
            if (hasInvalidEmails) {
              isInvalid = true
            }
          }
        } else {
          isEmpty = false // Not required if section is hidden
        }
      } else {
        isEmpty = !formData[field] || formData[field].trim() === ''
      }

      const fieldLabels = {
        customerName: 'Your Name',
        lawFirmName: isPrelit ? 'Collections Agency' : 'Law Firm Name',
        lawsuitState: isPrelit ? 'State for the debt' : 'Lawsuit State',
        lawsuitAmount: isPrelit ? 'Amount in collections' : 'Lawsuit Amount',
        lawyerEmail: isPrelit ? 'Collector Email' : 'Lawyer Email',
        civilNumber: isPrelit ? 'File Number' : 'Civil Number',
        plaintiffName: isPrelit ? 'Creditor Name' : 'Plaintiff Name',
        address: 'Address',
        ssn: 'Social Security Number',
        birthdate: 'Birthdate',
      }

      if (isEmpty) {
        missingFields.push(fieldLabels[field])
        // Mark field as invalid in the UI
        setValidatedFields(prev => ({
          ...prev,
          [field]: 'invalid',
        }))
      } else if (isInvalid) {
        invalidFields.push(fieldLabels[field])
        setValidatedFields(prev => ({
          ...prev,
          [field]: 'invalid',
        }))
      }
    })

    if (missingFields.length > 0) {
      showErrorMessage(`Please fill in the following required fields: ${missingFields.join(', ')}`)
      return
    }

    if (invalidFields.length > 0) {
      showErrorMessage(`Please correct the following fields: ${invalidFields.join(', ')}`)
      return
    }

    // Clear any previous errors
    setError(null)
    setShowError(false)

    document.getElementById('editor-form-hidden-content').value = JSON.stringify({ content: 'OK' })
    document.getElementById('editor-form').submit()
  }

  // Function to get input class based on validation state
  const getInputClass = (fieldName, baseClass = 'form-control') => {
    if (validatedFields[fieldName] === true) {
      return `${baseClass} is-valid`
    } else if (validatedFields[fieldName] === 'invalid') {
      return `${baseClass} is-invalid`
    } else {
      return baseClass
    }
  }

  // Law firm search function
  const searchLawFirms = async query => {
    if (!query || query.trim().length < 2) {
      setLawFirmSearchResults([])
      return
    }

    setLawFirmSearchLoading(true)

    try {
      const response = await fetch(`/api/law_firms/search?query=${encodeURIComponent(query)}`)

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`Error status ${response.status}: ${errorText}`)
        throw new Error(`Failed to search law firms: ${response.statusText}`)
      }

      const data = await response.json()
      setLawFirmSearchResults(data.results || [])
    } catch (error) {
      console.error('Error searching law firms:', error)
      setLawFirmSearchResults([])
    } finally {
      setLawFirmSearchLoading(false)
    }
  }

  // Function to handle law firm selection
  const handleLawFirmSelect = async lawFirm => {
    // Check if the law firm changed
    const lawFirmChanged = !selectedLawFirm || selectedLawFirm.id !== lawFirm.id

    // Update UI state
    setSelectedLawFirm(lawFirm)
    setFormData({
      ...formData,
      lawFirmName: lawFirm.name,
    })

    // Mark field as validated
    setValidatedFields(prev => ({
      ...prev,
      lawFirmName: true,
    }))

    // Update original value for law firm name
    updateOriginalValue('lawFirmName', lawFirm.name)

    // If law firm changed, clear lawyer emails to force re-entry
    if (lawFirmChanged) {
      setFormData(prev => ({
        ...prev,
        lawyerEmail: '',
      }))
      setAdditionalLawyerEmails([])
      setValidatedFields(prev => ({
        ...prev,
        lawyerEmail: 'invalid',
      }))

      // Also update original lawyer email when clearing
      updateOriginalValue('lawyerEmail', '')

      // Clear lawyer_email step when law firm changes
      try {
        fetch(`/api_controller/delete_lawyer_email/${documentId}`, {
          method: 'DELETE',
          headers: getHeaders(),
        })
      } catch (error) {
        console.error('Error clearing lawyer emails:', error)
      }
    }

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) {
      console.error('CSRF token not found')
    }

    // Save both the law firm name AND create the confirm law firm step
    try {
      const response = await fetch(`/api_controller/save_law_firm/${documentId}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          law_firm_name: lawFirm.name,
          selected_law_firm_id: lawFirm.id,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('Failed to save law firm:', data)
        throw new Error(data.error || 'Failed to save law firm')
      }

      showSuccessMessage('Law Firm Details')

      // Refresh the data to update the visibility of lawyer email section
      await refreshResponseData()
    } catch (error) {
      console.error('Error saving law firm:', error)
      // Fallback to the original method if the new endpoint fails
      saveData(
        'law_firm_name',
        {
          law_firm_name: lawFirm.name,
          selected_law_firm_id: lawFirm.id,
        },
        false,
        'Law Firm Details'
      )

      // Still try to refresh the data
      refreshResponseData().catch(e => console.error('Error refreshing data:', e))
    }
  }

  // Function to refresh response data from the API
  const refreshResponseData = async () => {
    try {
      const response = await fetch(
        `/api_controller/get_extracted_walk_through_step_responses/${documentId}`
      )

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      const data = await response.json()
      setResponses(data)

      // Update UI based on new data
      if (data && data.extracted_step_values) {
        const values = data.extracted_step_values

        // Update selected law firm if needed
        if (
          values.confirm_law_firm &&
          values.confirm_law_firm.formatted_value &&
          values.confirm_law_firm.formatted_value.id
        ) {
          setSelectedLawFirm({
            id: values.confirm_law_firm.formatted_value.id,
            name: values.confirm_law_firm.formatted_value.name,
            to_addresses: values.confirm_law_firm.formatted_value.to_addresses,
          })
        }
      }

      return data
    } catch (error) {
      console.error('Error refreshing response data:', error)
      throw error
    }
  }

  // Function to handle manual law firm name input
  const handleLawFirmNameChange = e => {
    const value = e.target.value
    handleInputChange('lawFirmName', value)

    // If typing manually, clear the selected law firm
    if (selectedLawFirm && selectedLawFirm.name !== value) {
      setSelectedLawFirm(null)
    }

    // Search for law firms as user types
    searchLawFirms(value)
  }

  // Function to handle manual law firm save on blur
  const handleLawFirmNameBlur = e => {
    // If a law firm was selected from dropdown, don't override it
    if (selectedLawFirm) return

    const value = e.target.value
    const lawFirmChanged = formData.lawFirmName !== value

    // If law firm changed via manual entry, clear lawyer emails
    if (lawFirmChanged) {
      setFormData(prev => ({
        ...prev,
        lawyerEmail: '',
      }))
      setAdditionalLawyerEmails([])
      setValidatedFields(prev => ({
        ...prev,
        lawyerEmail: false,
      }))

      // Clear lawyer_email step when law firm changes
      try {
        fetch(`/api_controller/delete_lawyer_email/${documentId}`, {
          method: 'DELETE',
          headers: getHeaders(),
        })
      } catch (error) {
        console.error('Error clearing lawyer emails:', error)
      }
    }

    // Otherwise save the text value normally, but explicitly pass null for selected_law_firm_id
    // to ensure any existing confirm step is removed
    if (value) {
      // Call the specialized endpoint
      try {
        fetch(`/api_controller/save_law_firm/${documentId}`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            law_firm_name: value,
            selected_law_firm_id: null, // Explicitly null to remove any confirm step
          }),
        })
          .then(response => {
            if (response.ok) {
              // Mark field as valid for manual entry
              setValidatedFields(prev => ({
                ...prev,
                lawFirmName: true,
              }))

              // Update original value after successful save
              updateOriginalValue('lawFirmName', value)

              showSuccessMessage('Law Firm Details')

              // Refresh data to update lawyer email visibility
              refreshResponseData().catch(e => console.error('Error refreshing data:', e))
            } else {
              // Mark field as invalid if save failed
              setValidatedFields(prev => ({
                ...prev,
                lawFirmName: 'invalid',
              }))
            }
          })
          .catch(error => {
            console.error('Error saving manual law firm name:', error)
            // Mark field as invalid on error
            setValidatedFields(prev => ({
              ...prev,
              lawFirmName: 'invalid',
            }))
          })
      } catch (error) {
        console.error('Error saving manual law firm name:', error)
        // Fall back to original method
        handleInputBlur('lawFirmName', value)
      }
    } else {
      handleInputBlur('lawFirmName', value)
    }
  }

  // Plaintiff search function
  const searchPlaintiffs = async query => {
    if (!query || query.trim().length < 2) {
      setPlaintiffSearchResults([])
      return
    }

    setPlaintiffSearchLoading(true)

    try {
      const response = await fetch(`/api/plaintiffs/search?query=${encodeURIComponent(query)}`)

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`Error status ${response.status}: ${errorText}`)
        throw new Error(`Failed to search plaintiffs: ${response.statusText}`)
      }

      const data = await response.json()
      setPlaintiffSearchResults(data.results || [])
    } catch (error) {
      console.error('Error searching plaintiffs:', error)
      setPlaintiffSearchResults([])
    } finally {
      setPlaintiffSearchLoading(false)
    }
  }

  // Function to handle plaintiff selection
  const handlePlaintiffSelect = async plaintiff => {
    // Update UI state
    setSelectedPlaintiff(plaintiff)
    setFormData({
      ...formData,
      plaintiffName: plaintiff.name,
    })

    // Mark field as validated
    setValidatedFields(prev => ({
      ...prev,
      plaintiffName: true,
    }))

    // Update original value for plaintiff name
    updateOriginalValue('plaintiffName', plaintiff.name)

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) {
      console.error('CSRF token not found')
    }

    // Save both the plaintiff name AND create the confirm plaintiff step
    try {
      const response = await fetch(`/api_controller/save_plaintiff/${documentId}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          plaintiff_name: plaintiff.name,
          selected_plaintiff_id: plaintiff.id,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('Failed to save plaintiff:', data)
        throw new Error(data.error || 'Failed to save plaintiff')
      }

      showSuccessMessage('Plaintiff Details')
    } catch (error) {
      console.error('Error saving plaintiff:', error)
      saveData(
        'plaintiff_name',
        {
          plaintiff_name: plaintiff.name,
        },
        false,
        'Plaintiff Name'
      )
    }
  }

  // Function to handle manual plaintiff name input
  const handlePlaintiffNameChange = e => {
    const value = e.target.value
    handleInputChange('plaintiffName', value)

    // If typing manually, clear the selected plaintiff
    if (selectedPlaintiff && selectedPlaintiff.name !== value) {
      setSelectedPlaintiff(null)

      // Ensure validation state is updated
      if (!value) {
        setValidatedFields(prev => ({
          ...prev,
          plaintiffName: requiredFields.includes('plaintiffName') ? 'invalid' : false,
        }))
      }
    }

    // Search for plaintiffs as user types
    searchPlaintiffs(value)
  }

  // Function to handle manual plaintiff save on blur
  const handlePlaintiffNameBlur = e => {
    // If a plaintiff was selected from dropdown, don't override it
    if (selectedPlaintiff) return

    const value = e.target.value

    // Otherwise save the text value normally, but explicitly pass null for selected_plaintiff_id
    // to ensure any existing confirm step is removed
    if (value) {
      // Call the specialized endpoint
      try {
        fetch(`/api_controller/save_plaintiff/${documentId}`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            plaintiff_name: value,
            selected_plaintiff_id: null, // Explicitly null to remove any confirm step
          }),
        })

        setValidatedFields(prev => ({
          ...prev,
          plaintiffName: true,
        }))

        showSuccessMessage('Plaintiff Details')
      } catch (error) {
        console.error('Error saving manual plaintiff name:', error)
        // Fall back to original method
        handleInputBlur('plaintiffName', value)
      }
    } else {
      handleInputBlur('plaintiffName', value)
    }
  }

  // Helper function to determine if lawyer email section should be shown
  const shouldShowLawyerEmail = () => {
    if (!responses || !responses.extracted_step_values) {
      return true // Show by default if no responses
    }

    const hasToAddresses =
      responses.extracted_step_values.confirm_law_firm?.formatted_value?.to_addresses === true
    const hasLawyerEmails =
      responses.extracted_step_values.lawyer_email?.inputs?.lawyer_emails &&
      responses.extracted_step_values.lawyer_email?.inputs?.lawyer_emails !== ''

    return !(hasToAddresses || (hasLawyerEmails && !formData.lawyerEmail))
  }

  // Handle phone number changes and validation
  const handlePhoneChange = e => {
    const value = e.target.value
    handleInputChange('phoneNumber', value)
  }

  const handlePhoneBlur = async () => {
    const phoneNumber = formData.phoneNumber

    // Check if phone number has changed
    if (phoneNumber === originalFormData.phoneNumber) {
      // Still mark as valid if it was previously valid
      if (phoneNumber.trim() !== '') {
        setValidatedFields(prev => ({
          ...prev,
          phoneNumber: true,
        }))
      }
      return
    }

    // Check if phone number is empty (now required)
    if (!phoneNumber || phoneNumber.trim() === '') {
      setValidatedFields(prev => ({
        ...prev,
        phoneNumber: 'invalid',
      }))
      return
    }

    try {
      const response = await fetch(`/api_controller/update_phone_number/${documentId}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          phone_number: phoneNumber,
          consents_to_sms: true, // Always consent when a phone number is provided
        }),
      })

      const data = await response.json()

      if (response.ok) {
        // Mark phone number as valid
        setValidatedFields(prev => ({
          ...prev,
          phoneNumber: true,
        }))

        // Update original value after successful save
        updateOriginalValue('phoneNumber', phoneNumber)

        showSuccessMessage('Phone Number')
      } else {
        // Show validation error
        setValidatedFields(prev => ({
          ...prev,
          phoneNumber: 'invalid',
        }))
        showErrorMessage(data.errors ? data.errors[0] : 'Invalid phone number format')
      }
    } catch (error) {
      console.error('Error validating phone number:', error)
      showErrorMessage('Failed to validate phone number')
    }
  }

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error}</div>

  return (
    <div className='document-summary col-md-7'>
      {/* Success Alert */}
      {saveSuccess && (
        <div
          className='alert alert-success text-center'
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            padding: '10px 20px',
            borderRadius: '4px',
            minWidth: '200px',
          }}
        >
          <i className='bi bi-check-circle-fill me-2'></i>
          {saveMessage}
        </div>
      )}

      {/* Error Alert - styled like success alert but with error colors */}
      {showError && (
        <div
          className='alert alert-danger text-center'
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            padding: '10px 20px',
            borderRadius: '4px',
            minWidth: '200px',
          }}
        >
          ⚠️&nbsp;{errorMessage}
        </div>
      )}

      {/* Keep the original error display for other errors */}
      {error && !showError && (
        <div className='alert alert-danger'>
          <i className='bi bi-exclamation-triangle-fill me-2'></i>
          {error}
        </div>
      )}

      {/* Add CSS styles for the search component */}
      <style jsx='true'>{`
        .search-result {
          cursor: pointer;
          transition: background-color 0.15s ease-in-out;
        }
        .search-result:hover {
          background-color: var(--bs-light);
        }
        .search-results {
          border-top: none;
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .valid-feedback.d-block {
          display: block;
        }
      `}</style>

      <p>This is the information that we extracted. Review and edit as needed.</p>

      {responses ? (
        <div className='responses-container'>
          <h3>{responses.extracted_step_values.customer_name.title}</h3>
          <input
            className={getInputClass('customerName')}
            type='text'
            id='customer-name'
            value={formData.customerName}
            onChange={e => handleInputChange('customerName', e.target.value)}
            onBlur={e => handleInputBlur('customerName', e.target.value)}
          />
          {validatedFields.customerName === true && <div className='valid-feedback'></div>}
          {validatedFields.customerName === 'invalid' && (
            <div className='invalid-feedback'>This field is required</div>
          )}

          <br />

          <h3>{isPrelit ? 'State for the debt' : 'Lawsuit State'}</h3>
          <select
            className={getInputClass('lawsuitState', 'form-select')}
            value={formData.lawsuitState}
            onChange={e => {
              const newValue = e.target.value
              // Update local state
              handleInputChange('lawsuitState', newValue)

              // Only save if value has changed
              if (newValue !== originalFormData.lawsuitState) {
                // Save immediately on change for dropdown
                saveData('lawsuit_state', { lawsuit_state: newValue }, false, 'Lawsuit State')
              } else {
                // Still mark as valid
                setValidatedFields(prev => ({
                  ...prev,
                  lawsuitState: true,
                }))
              }
            }}
          >
            <option value=''>Select a state</option>
            {supportedJurisdictions.map(jurisdiction => (
              <option key={jurisdiction} value={jurisdiction}>
                {jurisdiction}
              </option>
            ))}
          </select>
          {validatedFields.lawsuitState === true && <div className='valid-feedback'></div>}
          {validatedFields.lawsuitState === 'invalid' && (
            <div className='invalid-feedback'>Please select a state</div>
          )}

          <br />

          <h3>{isPrelit ? 'Amount in collections' : 'Lawsuit Amount'}</h3>
          <div
            className={
              validatedFields.lawsuitAmount === true
                ? 'is-valid'
                : validatedFields.lawsuitAmount === 'invalid'
                  ? 'is-invalid'
                  : ''
            }
          >
            <CurrencyInput
              normalSize={true}
              value={formData.lawsuitAmount}
              placeholder='Enter amount (minimum $100)'
              data-no-stimulus='true'
              onChange={e => {
                // Update local state with the raw value
                const rawValue = e.target.value
                setFormData({
                  ...formData,
                  lawsuitAmount: rawValue,
                })
              }}
              onBlur={() => {
                const rawValue = formData.lawsuitAmount

                // Check if value has actually changed before validating or saving
                const currentAmount = parseFloat(rawValue) || 0
                const originalAmount = parseFloat(originalFormData.lawsuitAmount) || 0

                if (currentAmount === originalAmount) {
                  // Value hasn't changed, don't validate or save
                  // But mark as valid if it meets requirements
                  if (currentAmount >= 100) {
                    setValidatedFields(prev => ({
                      ...prev,
                      lawsuitAmount: true,
                    }))
                  }
                  return
                }

                // Value has changed, proceed with validation and saving
                if (rawValue && rawValue.toString().trim() !== '' && currentAmount >= 100) {
                  const formattedValue = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(currentAmount)

                  saveData(
                    'lawsuit_amount',
                    {
                      lawsuit_amount: formattedValue,
                    },
                    false,
                    'Lawsuit Amount'
                  )
                } else {
                  // Handle empty field or amount < 100 - mark as invalid
                  setValidatedFields(prev => ({
                    ...prev,
                    lawsuitAmount: 'invalid',
                  }))
                }
              }}
              className={
                validatedFields.lawsuitAmount === true
                  ? 'is-valid'
                  : validatedFields.lawsuitAmount === 'invalid'
                    ? 'is-invalid'
                    : ''
              }
            />
          </div>
          {validatedFields.lawsuitAmount === true && <div className='valid-feedback'></div>}
          {validatedFields.lawsuitAmount === 'invalid' && (
            <div className='invalid-feedback'>Please enter a lawsuit amount of at least $100</div>
          )}

          <br />

          <div className='d-flex align-items-center'>
            <h3 className='mb-0'>{isPrelit ? 'File Number' : 'Civil Number'}</h3>
            <Tooltip
              text="The civil number may be made up of numbers and letters. It is usually located on the top half of the first page, beneath the court information. Sometimes it may be stamped on the margins of the document or in another location. The civil number may also be called the 'index no.,' 'case no.,' 'file no.,' or something else."
              width='320px'
              position='bottom'
              className='mb-1'
            />
          </div>
          <input
            id='civil_number'
            className={getInputClass('civilNumber')}
            type='text'
            value={formData.civilNumber}
            onChange={e => handleInputChange('civilNumber', e.target.value)}
            onBlur={e => handleInputBlur('civilNumber', e.target.value)}
          />
          {validatedFields.civilNumber === true && <div className='valid-feedback'></div>}
          {validatedFields.civilNumber === 'invalid' && (
            <div className='invalid-feedback'>Please enter the civil number</div>
          )}

          <br />

          <h3>{isPrelit ? 'Collections Agency' : 'Law Firm Name'}</h3>
          <div className='position-relative'>
            <LawFirmSearch
              value={formData.lawFirmName}
              onChange={handleLawFirmNameChange}
              onBlur={handleLawFirmNameBlur}
              onSelect={handleLawFirmSelect}
              results={lawFirmSearchResults}
              loading={lawFirmSearchLoading}
              inputClassName={getInputClass('lawFirmName')}
            />
          </div>
          {validatedFields.lawFirmName === true && <div className='valid-feedback d-block'></div>}
          {validatedFields.lawFirmName === 'invalid' && (
            <div className='invalid-feedback'>
              Please enter the name of the {isPrelit ? 'collections agency' : 'law firm'}.
            </div>
          )}

          <br />

          {/* Only show Lawyer Email section if to_addresses is not present on the law firm and no emails are already entered */}
          {shouldShowLawyerEmail() && (
            <>
              <h3>{isPrelit ? 'Collector Email' : 'Lawyer Email'}</h3>
              <div className='text-muted small mb-2'>
                {formData.lawFirmName && (
                  <span>
                    Enter the email address(es) for {isPrelit ? 'collectors' : 'lawyers'} at{' '}
                    {formData.lawFirmName}.{' '}
                  </span>
                )}
                If you cannot find the email(s) on your document, look them up with the help of our{' '}
                <a
                  href='https://www.solosuit.com/posts/find-attorney-suing-you'
                  target='_blank'
                  rel='noreferrer'
                >
                  blog post
                </a>
                .
              </div>
              <input
                className={getInputClass('lawyerEmail')}
                type='text'
                value={formData.lawyerEmail}
                onChange={e => handleInputChange('lawyerEmail', e.target.value)}
                onBlur={handleLawyerEmailBlur}
                placeholder='Primary lawyer email'
              />
              {validatedFields.lawyerEmail === true && <div className='valid-feedback'></div>}
              {validatedFields.lawyerEmail === 'invalid' && (
                <div className='invalid-feedback'>
                  Please enter the email address(es) for the {isPrelit ? 'collectors' : 'lawyers'}{' '}
                  listed on your document.
                </div>
              )}
              {additionalLawyerEmails.map((email, index) => (
                <div className='input-group mt-2' key={`lawyer-email-${index}`}>
                  <input
                    className={getInputClass('lawyerEmail')}
                    type='text'
                    value={email}
                    onChange={e => updateLawyerEmail(index, e.target.value)}
                    onBlur={handleLawyerEmailBlur}
                    placeholder='Additional lawyer email'
                  />
                  <button
                    className='btn btn-outline-danger'
                    type='button'
                    onClick={() => {
                      const updatedEmails = additionalLawyerEmails.filter((_, i) => i !== index)
                      setAdditionalLawyerEmails(updatedEmails)

                      // Save immediately after removing an email
                      const allEmails = [formData.lawyerEmail, ...updatedEmails].filter(
                        email => email.trim() !== ''
                      )
                      const emailString = allEmails.join(';')
                      saveData(
                        'lawyer_email',
                        { lawyer_emails: emailString },
                        false,
                        'Lawyer Email'
                      )
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}

              <button className='btn btn-link p-0 mt-1' type='button' onClick={addLawyerEmail}>
                <i className='bi bi-plus-circle me-1'></i>Add another email
              </button>

              <br />
              <br />
            </>
          )}

          <div className='d-flex align-items-center'>
            <h3 className='mb-0'>{isPrelit ? 'Creditor Name' : 'Plaintiff Name'}</h3>
            <Tooltip
              text="The name will appear above your name on the first page of the Complaint. The business suing you is the 'Plaintiff' in this lawsuit."
              width='320px'
              position='bottom'
              className='mb-1'
            />
          </div>
          <div className='position-relative'>
            <LawFirmSearch
              value={formData.plaintiffName}
              onChange={handlePlaintiffNameChange}
              onBlur={handlePlaintiffNameBlur}
              onSelect={handlePlaintiffSelect}
              results={plaintiffSearchResults}
              loading={plaintiffSearchLoading}
              inputClassName={getInputClass('plaintiffName')}
              placeholder='Search plaintiff name'
            />
            {validatedFields.plaintiffName === true && (
              <div className='valid-feedback d-block'></div>
            )}
            {validatedFields.plaintiffName === 'invalid' && (
              <div className='invalid-feedback'>Please enter the plaintiff name</div>
            )}
          </div>

          <br />
          <br />

          <div className='card p-4 mb-4 border-secondary'>
            <Alert type='primary'>
              These details are required by the collector to locate your account
            </Alert>

            <div className='d-flex align-items-center'>
              <h3 className='mb-0'>Address</h3>
              <Tooltip
                text='Debt collectors usually require this to verify that you are the person connected to the debt.'
                width='320px'
                position='bottom'
                className='mb-1'
              />
            </div>
            <div>
              <GooglePlacesAutocomplete
                value={formData.address.street}
                onChange={e => {
                  setFormData(prev => ({
                    ...prev,
                    address: {
                      ...prev.address,
                      street: e.target.value,
                    },
                  }))
                }}
                onPlaceSelected={address => {
                  setFormData(prev => ({
                    ...prev,
                    address: {
                      street: address.address_line1 || '',
                      city: address.city || '',
                      state: address.state || '',
                      zipCode: address.zip_code || '',
                    },
                  }))

                  // Save the address after autocomplete selection
                  saveData(
                    'customer_mailing_address',
                    {
                      customer_street: address.address_line1,
                      customer_city: address.city,
                      customer_state: address.state,
                      customer_zip_code: address.zip_code,
                    },
                    false,
                    'Address'
                  )
                }}
                isInvalid={validatedFields.address === 'invalid'}
                error={validatedFields.address === 'invalid' ? 'Please provide your address' : ''}
              />
              <br />
              <div className='row'>
                <div className='col-md-5 mb-2'>
                  <FloatingLabel controlId='floatingCity' label='City'>
                    <Form.Control
                      type='text'
                      value={formData.address.city}
                      onChange={e => {
                        const { value } = e.target
                        setFormData(prev => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            city: value,
                          },
                        }))
                      }}
                      onBlur={() => {
                        saveData(
                          'customer_mailing_address',
                          {
                            customer_street: formData.address.street,
                            customer_city: formData.address.city,
                            customer_state: formData.address.state,
                            customer_zip_code: formData.address.zipCode,
                          },
                          false,
                          'Address'
                        )
                      }}
                      isInvalid={validatedFields.address === 'invalid'}
                      placeholder='City'
                    />
                  </FloatingLabel>
                </div>
                <div className='col-md-3 mb-2'>
                  <FloatingLabel controlId='floatingState' label='State'>
                    <Form.Control
                      type='text'
                      value={formData.address.state}
                      onChange={e => {
                        const { value } = e.target
                        setFormData(prev => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            state: value,
                          },
                        }))
                      }}
                      onBlur={() => {
                        saveData(
                          'customer_mailing_address',
                          {
                            customer_street: formData.address.street,
                            customer_city: formData.address.city,
                            customer_state: formData.address.state,
                            customer_zip_code: formData.address.zipCode,
                          },
                          false,
                          'Address'
                        )
                      }}
                      isInvalid={validatedFields.address === 'invalid'}
                      placeholder='State'
                    />
                  </FloatingLabel>
                </div>
                <div className='col-md-4 mb-2'>
                  <FloatingLabel controlId='floatingZip' label='ZIP Code'>
                    <Form.Control
                      type='text'
                      value={formData.address.zipCode}
                      onChange={e => {
                        const { value } = e.target
                        setFormData(prev => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            zipCode: value,
                          },
                        }))
                      }}
                      onBlur={() => {
                        saveData(
                          'customer_mailing_address',
                          {
                            customer_street: formData.address.street,
                            customer_city: formData.address.city,
                            customer_state: formData.address.state,
                            customer_zip_code: formData.address.zipCode,
                          },
                          false,
                          'Address'
                        )
                      }}
                      isInvalid={validatedFields.address === 'invalid'}
                      placeholder='ZIP Code'
                    />
                  </FloatingLabel>
                </div>
              </div>
              {validatedFields.address === true && <div className='valid-feedback'></div>}
              {validatedFields.address === 'invalid' && (
                <div className='invalid-feedback'>Please provide your address</div>
              )}
            </div>

            <br />

            <div className='d-flex align-items-center'>
              <h3 className='mb-0'>Phone Number</h3>
              <Tooltip
                text='By providing your phone number, you authorize Solo to send text messages containing critical updates regarding your services. Message and data rates may apply.'
                width='320px'
                position='bottom'
                className='mb-1'
              />
            </div>
            <div
              className={
                validatedFields.phoneNumber === true
                  ? 'is-valid'
                  : validatedFields.phoneNumber === 'invalid'
                    ? 'is-invalid'
                    : ''
              }
            >
              <PhoneNumberInput
                normalSize={true}
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                placeholder='(123) 456-7890'
                className={`mt-2 ${
                  validatedFields.phoneNumber === true
                    ? 'is-valid'
                    : validatedFields.phoneNumber === 'invalid'
                      ? 'is-invalid'
                      : ''
                }`}
              />
            </div>
            {validatedFields.phoneNumber === 'invalid' && (
              <div className='invalid-feedback d-block'>Please enter a valid US phone number</div>
            )}
            {validatedFields.phoneNumber === true && <div className='valid-feedback d-block'></div>}

            <br />

            <div className='d-flex align-items-center'>
              <h3 className='mb-0'>
                {responses.extracted_step_values.social_security_number.title}
              </h3>
              <Tooltip
                text='Debt collectors usually require this to verify that you are the person connected to the debt. Collectors use the full social security number to match against the full social security number they already have on file. Without the full social security number, collectors may not be able to settle.'
                width='320px'
                position='bottom'
                className='mb-1'
              />
            </div>
            <div>
              <SSNInput
                fullSSN={formData.ssn.fullSSN}
                lastFour={formData.ssn.lastFour}
                onChange={handleSSNChange}
                onBlur={handleSSNBlur}
                errors={{}}
                initialShowFullSSN={formData.ssn.showFullSSN}
                isValid={validatedFields.ssn === true}
                isInvalid={validatedFields.ssn === 'invalid'}
              />
              {validatedFields.ssn === 'invalid' && (
                <div className='invalid-feedback d-block'>
                  Please provide your SSN or last 4 digits
                </div>
              )}
            </div>

            <br />

            <div className='d-flex align-items-center'>
              <h3 className='mb-0'>Birthdate</h3>
              <Tooltip
                text='Debt collectors usually require this to verify that you are the person connected to the debt.'
                width='320px'
                position='bottom'
                className='mb-1'
              />
            </div>
            <input
              type='date'
              id='birthdate'
              className={getInputClass('birthdate')}
              value={formData.birthdate}
              onChange={e => {
                const value = e.target.value
                handleInputChange('birthdate', value)
              }}
              onBlur={() => {
                const value = formData.birthdate
                saveData('birthdate', { birthdate: value }, true, 'Birthdate')
              }}
              min={
                new Date(Date.now() - 125 * 365.25 * 24 * 60 * 60 * 1000)
                  .toISOString()
                  .split('T')[0]
              }
              max={
                new Date(Date.now() - 16 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
              }
            />
            {validatedFields.birthdate === true && <div className='valid-feedback'></div>}
            {validatedFields.birthdate === 'invalid' && (
              <div className='invalid-feedback'>Please provide your birthdate</div>
            )}
          </div>

          <br />

          <form onSubmit={handleSubmit}>
            <div className='card p-3 mb-3 border'>
              <div className='form-check'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  id='confirmAccuracy'
                  checked={confirmInfoAccuracy}
                  onChange={e => setConfirmInfoAccuracy(e.target.checked)}
                  required
                />
                <label className='form-check-label' htmlFor='confirmAccuracy'>
                  I agree that the above information is correct and accurate
                </label>
              </div>
            </div>
            <br />
            <button type='submit' className='btn btn-primary w-100' disabled={!confirmInfoAccuracy}>
              Continue
            </button>
          </form>
        </div>
      ) : (
        <div>No data available</div>
      )}
    </div>
  )
}

export default DocumentSummaryRoot
